import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import { Grid, Typography, Tooltip } from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import * as H from "helper";

const useStyles = makeStyles((theme) =>
  createStyles({
    box: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1),
      borderRight: "1px solid #dcdcdc",
    },
    title: {
      display: "block",
      height: 32,
      fontSize: 14,
    },
    titleRow: {
      display: "flex",
      alignItems: "right",
    },
    helpIcon: {
      marginLeft: theme.spacing(0.5),
      fontSize: 16,
      color: "#9e9e9e",
      cursor: "pointer",
    },
    value: {
      paddingTop: 0,
      paddingBottom: 0,
      minWidth: 150,
      whiteSpace: "pre-line",
    },
  })
);

// Tooltip 用の説明テキスト
const TOOLTIP_TEXT = {
  method:
    "このシーズンで設定されているレビュー方法です（AIのみ／運用者のみ／AIレビューNG時に運用者レビュー など）。",
  total:
    "この期間・シーズンでレビュー対象になったレビューIDの総数です。AIレビューと運用者レビューをID単位で重複を除いて集計しています。",
  aiCompleted:
    "AIによる審査が完了したレビューIDの数です（AIのみのレビューと、AI→運用者レビューの両方を含みます）。",
  aiUncompleted:
    "AIレビュー対象のうち、まだAIによる審査が完了していないレビューIDの数です。",
  opCompleted:
    "運用者による審査が完了したレビューIDの数です。AIレビュー完了としてカウントしているIDは含めません。",
  opUncompleted:
    "OPERATOR_REVIEW_ONLY と AI→運用者レビューのうち、AIレビューは完了しているが、まだ運用者レビューが完了していないレビューIDの数です（AIレビュー未完了のIDは含みません）。",
};

const ReviewStatusSummary = ({ reviewSummaryBySeason }) => {
  const classes = useStyles();

  const reviewMethodLabel =
    reviewSummaryBySeason && reviewSummaryBySeason.length > 0
      ? reviewSummaryBySeason.map((m) => `・${m.reviewTypeName}`).join("\n")
      : "-";

  const formatCount = (value) =>
    value != null ? H.FormatNumStrHelper.formatCommaThousand(value) : "-";

  const getSummaryNumbers = (methods = []) => {
    const acc = {
      reviewTotal: 0,
      aiCompleted: 0,
      aiUncompleted: 0,
      opCompleted: 0,
      opUncompleted: 0,
    };

    methods.forEach((method) => {
      const {
        review_flow_type,
        completedCount = 0,
        uncompletedCount = 0,
        aiReviewNotCompleted = 0,
        aiCompletedButNotOperatorCompleted = 0,
      } = method;

      // このフローでレビュー対象になった総数
      const methodTotal = completedCount + uncompletedCount;

      // レビュー総数は全フロー共通で足す（重複なし）
      acc.reviewTotal += methodTotal;

      switch (review_flow_type) {
        case "AI_REVIEW_ONLY": {
          // AI-only は AI 側の完了・未完了にそのまま反映
          acc.aiCompleted += completedCount;
          acc.aiUncompleted += uncompletedCount;
          break;
        }

        case "OPERATOR_REVIEW_ONLY": {
          // 運用者のみフロー
          acc.opCompleted += completedCount;
          acc.opUncompleted += uncompletedCount;
          break;
        }

        case "OPERATOR_REVIEW_IF_AI_REVIEW_NG": {
          // Hybrid フローの内訳:
          // A0  : AI 未完了         = aiReviewNotCompleted
          // A1O0: AI 完了・運用者未完 = aiCompletedButNotOperatorCompleted
          // A1O1: AI 完了・運用者完了 = completedCount

          const A0 = aiReviewNotCompleted;
          const A1O0 = aiCompletedButNotOperatorCompleted;
          const A1O1 = completedCount;

          // ■ AI 側の指標
          acc.aiCompleted += A1O1 + A1O0; // AIが完了したIDすべて
          acc.aiUncompleted += A0; // AIがまだのID

          // ■ 運用者側の指標（AI完了IDと重複させない）
          // 完了: OPERATOR_REVIEW_ONLY のみ（Hybrid分は AI 完了に含めるため除外）
          // 未完了: 「AI完了済みだが運用者未完了」のIDだけ足す
          acc.opUncompleted += A1O0;

          break;
        }

        default:
          // WITHOUT_REVIEW 等はサマリー対象外
          break;
      }
    });

    return acc;
  };

  const {
    reviewTotal,
    aiCompleted,
    aiUncompleted,
    opCompleted,
    opUncompleted,
  } = getSummaryNumbers(reviewSummaryBySeason || []);

  const renderTitle = (label, tooltip) => (
    <div className={classes.titleRow}>
      <Typography variant="caption" className={classes.title}>
        {label}
      </Typography>
      <Tooltip title={tooltip} arrow>
        <HelpOutlineIcon className={classes.helpIcon} />
      </Tooltip>
    </div>
  );

  return (
    <Grid container spacing={1}>
      <Grid item sm={12}>
        <Grid container spacing={1}>
          {/* レビュー方法 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle("レビュー方法", TOOLTIP_TEXT.method)}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" className={classes.value}>
                    {reviewMethodLabel}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* レビュー総数 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle("レビュー総数", TOOLTIP_TEXT.total)}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.value}>
                    {formatCount(reviewTotal)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* AIレビュー完了数 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle(
                    "AIレビュー完了数",
                    TOOLTIP_TEXT.aiCompleted
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.value}>
                    {formatCount(aiCompleted)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* AIレビュー未完了数 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle(
                    "AIレビュー未完了数",
                    TOOLTIP_TEXT.aiUncompleted
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.value}>
                    {formatCount(aiUncompleted)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* 運用者レビュー完了数 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle(
                    "運用者レビュー完了数",
                    TOOLTIP_TEXT.opCompleted
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.value}>
                    {formatCount(opCompleted)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>

          {/* 運用者レビュー未完了数 */}
          <Grid item sm={6} md={4} lg={2}>
            <div className={classes.box}>
              <Grid container direction="row">
                <Grid item xs={12}>
                  {renderTitle(
                    "運用者レビュー未完了数",
                    TOOLTIP_TEXT.opUncompleted
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.value}>
                    {formatCount(opUncompleted)}
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

ReviewStatusSummary.propTypes = {
  classes: PropTypes.object,
};

const mapStateToProps = (state) => ({
  reviewSummaryBySeason: state.pictree.reviewSummaryBySeason,
});

const mapDispatchToProps = (dispatch) => ({
  //setCurrentModule: (param) => dispatch(setCurrentModule(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(ReviewStatusSummary));
